import React, { useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArtworkUpload } from "../components/ArtworkUpload";
import {
  SIGN_SIZES, SIGN_QTYS, SIGN_SIDES, SIGN_CUTS,
  signUnitPrice, signLineTotal, formatAUDexact, SIGN_PRODUCTS,
  EYELET_PRICE, EYELET_OPTIONS,
} from "../data/signsPricing";
import { Check, Scissors, Layers, Circle } from "lucide-react";

export default function SignBuilder({ product }) {
  const info = SIGN_PRODUCTS[product];
  const [size, setSize] = useState("A2");
  const [qty, setQty] = useState(5);
  const [customQty, setCustomQty] = useState(""); // user-typed when qty === "other"
  const [sides, setSides] = useState("single");
  const [cutting, setCutting] = useState("standard");
  const [eyelets, setEyelets] = useState(0);
  const [file, setFile] = useState(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const [customW, setCustomW] = useState("");
  const [customH, setCustomH] = useState("");
  const isCustomSize = size === "custom";
  const isCustomQty = qty === "other";
  const needsQuote = isCustomQty || isCustomSize;
  const sizeLabel = isCustomSize ? `Custom ${customW || "?"} × ${customH || "?"} mm` : size;
  const effectiveQty = isCustomQty ? (parseInt(customQty, 10) || 0) : qty;

  const unit = useMemo(() => needsQuote ? null : signUnitPrice(product, size, qty, sides), [product, size, qty, sides, needsQuote]);
  const subtotal = useMemo(() => needsQuote ? null : signLineTotal(product, size, qty, sides, eyelets), [product, size, qty, sides, eyelets, needsQuote]);
  const eyeletCharge = (product === "corflute" && !needsQuote) ? eyelets * EYELET_PRICE * qty : 0;
  const gst = subtotal != null ? subtotal * 0.1 : null;
  const total = subtotal != null ? subtotal + gst : null;

  const addToCart = () => {
    if (!file) { toast.error("Please upload your artwork first"); return; }
    if (needsQuote) {
      const n = isCustomQty ? parseInt(customQty, 10) : qty;
      if (!n || n < 1) { toast.error("Please enter a valid quantity"); return; }
      if (isCustomSize && (!parseInt(customW, 10) || !parseInt(customH, 10))) { toast.error("Please enter the width and height of your sign"); return; }
      addItem({
        product_type: product,
        product_name: `${info.name} · ${sizeLabel}`,
        shape: cutting === "custom" ? "Custom cut" : "Standard cut",
        size: sizeLabel,
        quantity: n,
        unit_price: 0,
        line_total: 0,
        print_sides: sides,
        cutting,
        eyelets: product === "corflute" ? eyelets : 0,
        needs_quote: true,
        artwork_file_id: file.id,
        artwork_filename: file.name,
      });
      toast.success(`${n} × ${info.shortName} ${sizeLabel} added — we'll quote you`);
      navigate("/cart");
      return;
    }
    if (subtotal == null) { toast.error("Please choose a valid size and quantity"); return; }
    addItem({
      product_type: product,
      product_name: `${info.name} · ${size}`,
      shape: cutting === "custom" ? "Custom cut" : "Standard cut",
      size,
      quantity: qty,
      unit_price: unit,
      line_total: subtotal,
      print_sides: sides,
      cutting,
      eyelets: product === "corflute" ? eyelets : 0,
      artwork_file_id: file.id,
      artwork_filename: file.name,
    });
    toast.success(`${qty} × ${info.shortName} ${size} added to cart`);
    navigate("/cart");
  };

  return (
    <div className="App">
      <Header />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-6">
        <div className="eyebrow" data-testid="sign-eyebrow">{info.shortName} · Custom print</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-2 tracking-tight">{info.headline}</h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl">{info.tagline}</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT — product + ideal uses */}
        <div className="lg:col-span-6 space-y-6">
          <div className="sf-card overflow-hidden">
            <div className="aspect-[5/4] relative bg-[#F6F8FD]">
              <img
                src={info.image}
                alt={info.imageAlt}
                className="w-full h-full object-cover"
                data-testid="sign-product-photo"
              />
              <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/80 backdrop-blur text-white text-[10px] font-bold uppercase tracking-widest">
                {info.imageTag}
              </div>
            </div>
          </div>

          <div className="sf-card p-6">
            <div className="eyebrow mb-3">Ideal for</div>
            <ul className="grid grid-cols-2 gap-2 text-sm text-slate-700">
              {info.uses.map((u) => (
                <li key={u} className="flex items-center gap-2"><Check size={14} className="text-[#10B981]"/> {u}</li>
              ))}
            </ul>
          </div>

          <div className="sf-card p-6">
            <div className="eyebrow mb-3">About this product</div>
            <ul className="space-y-2 text-sm text-slate-700">
              {[info.boardLabel, "Full-colour print", "Single or double-sided", "Standard or custom cut — same price", "Printed locally in Australia"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check size={16} className="text-[#10B981]" />{t}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT — configurator */}
        <div className="lg:col-span-6 space-y-6">
          <div className="sf-card p-6 space-y-5">
            <div>
              <div className="eyebrow mb-3">Step 1 — Size</div>
              <div className="flex flex-wrap gap-2" data-testid="sign-size-options">
                {SIGN_SIZES.map((s) => (
                  <button key={s} onClick={() => setSize(s)} className={`chip ${size === s ? "chip-active" : ""}`} data-testid={`sign-size-${s}`}>{s}</button>
                ))}
                <button onClick={() => setSize("custom")} className={`chip ${isCustomSize ? "chip-active" : ""}`} data-testid="sign-size-custom">Custom</button>
              </div>
              {isCustomSize && (
                <div className="mt-3" data-testid="sign-size-custom-wrap">
                  <div className="grid grid-cols-2 gap-2">
                    {[["Width (mm)", customW, setCustomW, "sign-size-custom-width", "e.g. 600"], ["Height (mm)", customH, setCustomH, "sign-size-custom-height", "e.g. 900"]].map(([label, val, set, tid, ph]) => (
                      <label key={tid} className="block">
                        <span className="eyebrow block mb-1">{label}</span>
                        <input
                          type="number" inputMode="numeric" min="1" placeholder={ph}
                          value={val}
                          onChange={(e) => set(e.target.value.replace(/\D/g, "").slice(0, 5))}
                          className="w-full px-4 py-3 rounded-xl border border-[#E2DDD5] bg-white focus:outline-none"
                          data-testid={tid}
                        />
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Need a non-standard size? Pick <strong>Custom</strong>, type your width and height, upload your artwork and we'll come back to you with a tailored quote.</p>
                </div>
              )}
            </div>

            <div>
              <div className="eyebrow mb-3">Step 2 — Quantity</div>
              <div className="grid grid-cols-4 gap-2" data-testid="sign-qty-options">
                {SIGN_QTYS.map((q) => (
                  <button key={q} onClick={() => setQty(q)} className={`chip justify-center ${qty === q ? "chip-active" : ""}`} data-testid={`sign-qty-${q}`}>{q}</button>
                ))}
                <button
                  onClick={() => setQty("other")}
                  className={`chip justify-center ${isCustomQty ? "chip-active" : ""}`}
                  data-testid="sign-qty-other"
                >Other</button>
              </div>
              {isCustomQty && (
                <div className="mt-3" data-testid="sign-qty-other-input-wrap">
                  <label className="block">
                    <span className="eyebrow block mb-1">Enter your quantity</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      placeholder="e.g. 150"
                      value={customQty}
                      onChange={(e) => setCustomQty(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="w-full px-4 py-3 rounded-xl border border-[#E2DDD5] bg-white focus:outline-none"
                      data-testid="sign-qty-other-input"
                    />
                  </label>
                  <p className="text-xs text-slate-500 mt-2">Not sure on pricing? Pick <strong>Other</strong>, type your quantity, upload your artwork and we'll come back to you with a tailored quote.</p>
                </div>
              )}
            </div>

            <div>
              <div className="eyebrow mb-3">Step 3 — Print sides</div>
              <div className="grid grid-cols-2 gap-2" data-testid="sign-sides-options">
                {SIGN_SIDES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSides(s.id)}
                    className={`rounded-xl border px-4 py-3 text-sm font-semibold text-left transition ${sides === s.id ? "bg-[color:var(--brand)] text-white border-transparent" : "bg-white border-[#E2DDD5] hover:border-[color:var(--brand)]"}`}
                    data-testid={`sign-sides-${s.id}`}
                  >
                    <div className="flex items-center gap-2"><Layers size={14}/> {s.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="eyebrow mb-3">Step 4 — Cutting</div>
              <div className="grid grid-cols-2 gap-2" data-testid="sign-cut-options">
                {SIGN_CUTS.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCutting(c.id)}
                    className={`rounded-xl border px-4 py-3 text-sm font-semibold text-left transition ${cutting === c.id ? "bg-[color:var(--brand)] text-white border-transparent" : "bg-white border-[#E2DDD5] hover:border-[color:var(--brand)]"}`}
                    data-testid={`sign-cut-${c.id}`}
                  >
                    <div className="flex items-center gap-2"><Scissors size={14}/> {c.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {product === "corflute" && (
              <div data-testid="sign-eyelets-step">
                <div className="eyebrow mb-3">Step 5 — Eyelets</div>
                <div className="grid grid-cols-5 gap-2" data-testid="sign-eyelets-options">
                  {EYELET_OPTIONS.map((n) => (
                    <button
                      key={n}
                      onClick={() => setEyelets(n)}
                      className={`rounded-xl border px-3 py-3 text-sm font-semibold text-center transition ${eyelets === n ? "bg-[color:var(--brand)] text-white border-transparent" : "bg-white border-[#E2DDD5] hover:border-[color:var(--brand)]"}`}
                      data-testid={`sign-eyelets-${n}`}
                    >
                      <div className="flex items-center justify-center gap-1"><Circle size={10} className={eyelets === n ? "" : "opacity-40"}/>{n === 0 ? "None" : `${n}`}</div>
                    </button>
                  ))}
                </div>
                {eyelets > 0 && !needsQuote && (
                  <div className="mt-2 text-xs text-slate-500">
                    {eyelets} eyelets × {qty} boards × $1 = <span className="font-semibold text-slate-800 price-tick">{formatAUDexact(eyelets * qty * EYELET_PRICE)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* UPLOAD */}
          <div className="sf-card p-6">
            <div className="eyebrow mb-3">Step {product === "corflute" ? "6" : "5"} — Upload artwork</div>
            <ArtworkUpload file={file} setFile={setFile} />
          </div>

          {/* PRICE */}
          <div className="sf-card p-6">
            <div className="eyebrow">Live quote</div>
            {needsQuote ? (
              <>
                <div className="mt-2 flex items-baseline gap-2">
                  <div className="font-display text-3xl sm:text-4xl font-black" data-testid="sign-custom-quote-headline">Custom quote</div>
                </div>
                <div className="text-slate-600 mt-2 text-sm" data-testid="sign-custom-quote-note">
                  For non-standard {isCustomSize && isCustomQty ? "sizes and quantities" : isCustomSize ? "sizes" : "quantities"} we'll come back to you with a tailored price. Fill out your details at checkout and we'll be in touch.
                </div>
              </>
            ) : (
              <>
                <div className="mt-2 flex items-baseline gap-2">
                  <div className="font-display text-5xl font-black price-tick" data-testid="sign-total-exgst">{formatAUDexact(subtotal)}</div>
                  <div className="text-sm text-slate-500 font-semibold">ex GST</div>
                </div>
                <div className="text-slate-600 mt-1 text-sm" data-testid="sign-unit-price">{formatAUDexact(unit)} per board × {qty}</div>
              </>
            )}

            <div className="mt-5 space-y-2 text-sm">
              <Row label="Product" value={info.name} testid="sign-summary-product"/>
              <Row label="Size" value={isCustomSize ? (customW && customH ? `${sizeLabel} (quote)` : "Enter size above") : size} testid="sign-summary-size"/>
              <Row label="Quantity" value={isCustomQty ? (customQty ? `${Number(customQty).toLocaleString()} (quote)` : "Enter quantity above") : qty.toLocaleString()} testid="sign-summary-qty"/>
              <Row label="Print sides" value={sides === "double" ? "Double-sided" : "Single-sided"} testid="sign-summary-sides"/>
              <Row label="Cutting" value={cutting === "custom" ? "Custom cut to shape" : "Standard rectangular"} testid="sign-summary-cut"/>
              {product === "corflute" && (
                <Row label="Eyelets" value={eyelets === 0 ? "None" : needsQuote ? `${eyelets} per board` : `${eyelets} per board (+${formatAUDexact(eyeletCharge)})`} testid="sign-summary-eyelets"/>
              )}
              <Row label="Artwork" value={file ? file.name : "Not uploaded"} testid="sign-summary-artwork"/>
            </div>

            {!needsQuote && (
              <>
                <div className="h-px bg-[#E2DDD5] my-4"/>
                <div className="space-y-1 text-sm">
                  <Row label="Subtotal (ex GST)" value={formatAUDexact(subtotal)} testid="sign-row-subtotal"/>
                  <Row label="GST (10%)" value={formatAUDexact(gst)} testid="sign-row-gst"/>
                  <div className="flex justify-between items-baseline pt-2">
                    <div className="font-display font-bold">Total inc GST</div>
                    <div className="font-display font-black text-2xl price-tick" data-testid="sign-total-incgst">{formatAUDexact(total)}</div>
                  </div>
                </div>
              </>
            )}

            <button onClick={addToCart} className="btn-primary w-full mt-5" data-testid="sign-add-to-cart">
              {needsQuote ? "Request quote" : "Add to Cart"}
            </button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between border-b border-dashed border-[#E2DDD5] py-2" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}
